import { useTranslation } from 'react-i18next';
import type { Facility } from '../../types/facility';

interface FacilityCardProps {
  facility: Facility;
  onEdit: () => void;
  onDelete: () => void;
}

export function FacilityCard({ facility, onEdit, onDelete }: FacilityCardProps) {
  const { t } = useTranslation();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(price);
  };

  return (
    <div className="bg-surface-card rounded-lg p-4 hover:bg-surface-card-hover transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Name and Type */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-ink-primary font-medium truncate">{facility.name}</h3>
            <span className="text-xs px-2 py-0.5 bg-surface-app-elevated rounded text-ink-primary">
              {facility.type}
            </span>
          </div>

          {/* Address */}
          <p className="text-sm text-ink-secondary truncate">
            {facility.street}, {facility.city} {facility.postalCode}
          </p>

          {/* Price Info */}
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className="text-brand-primary font-medium">
              {formatPrice(facility.grossPricePerHour ?? facility.pricePerHour)}/h
            </span>
            {facility.vatRate && (
              <span className="text-ink-muted">
                VAT: {facility.vatRate}%
              </span>
            )}
            {facility.maxUsers && (
              <span className="text-ink-muted">
                {t('facilities.maxUsers')}: {facility.maxUsers}
              </span>
            )}
          </div>

          {/* Description */}
          {facility.description && (
            <p className="text-sm text-ink-muted mt-2 line-clamp-2">
              {facility.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-2 text-ink-secondary hover:text-brand-primary hover:bg-surface-card-hover rounded-lg transition-colors"
            title={t('common.edit')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-ink-secondary hover:text-brand-warm-deep hover:bg-surface-card-hover rounded-lg transition-colors"
            title={t('common.delete')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
